// Live call-out cost estimate for the office forms (display only). MIRRORS the
// authoritative server module server/src/modules/pricing/calloutRates.ts -- the
// server recomputes the figure and is what the Stripe payment link actually
// charges, so a drift here is only cosmetic. Kept JSX-free so the page and its
// test share one source of truth (the customerForm.ts pattern). Rates are the
// locked matrix.
//
//   Non-contract:          first £326/£250; OOH £652/£500. Travel £120/hr, OOH £240/hr.
//   In-contract Core:      first £163/£125; OOH £326/£250. Travel £60/hr, flat at any time.
//   In-contract Care:      first £138.55/£106.25; OOH £277.10/£212.50. Travel £60/hr, flat.
//   In-contract Complete:  bundled, no charge.
//   Out of hours always doubles LABOUR; it also doubles NON-CONTRACT travel (Session AKN,
//   Director decision, 19 August 2026). In-contract travel stays flat at any time of day.

export const TRAVEL_MINS_OPTIONS = [0, 15, 30, 45, 60, 75, 90, 105, 120] as const;

export type CalloutTier = "core" | "care" | "complete";

const IN_CONTRACT_FIRST: Record<CalloutTier, number> = { core: 163, care: 138.55, complete: 0 };
const IN_CONTRACT_ADDITIONAL: Record<CalloutTier, number> = { core: 125, care: 106.25, complete: 0 };
const NON_CONTRACT_FIRST = 326;
const NON_CONTRACT_ADDITIONAL = 250;
const TRAVEL_PER_HOUR_IN_CONTRACT = 60;
const TRAVEL_PER_HOUR_NON_CONTRACT = 120;
const VAT_RATE = 0.2;

export interface CalloutEstimateInput {
  contract: boolean;
  tier?: CalloutTier | null;
  outOfHours: boolean;
  travelMins: number;
}

export interface CalloutEstimate {
  bundled: boolean;
  firstHourNet: number;
  additionalHourNet: number;
  travelNet: number;
  totalNet: number;
  totalGross: number;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function estimateCallout(input: CalloutEstimateInput): CalloutEstimate {
  const travelMins = Math.min(120, Math.max(0, Math.round((input.travelMins || 0) / 15) * 15));

  if (input.contract && input.tier === "complete") {
    return { bundled: true, firstHourNet: 0, additionalHourNet: 0, travelNet: 0, totalNet: 0, totalGross: 0 };
  }

  let baseFirst: number;
  let baseAdd: number;
  let travelRate: number;
  if (input.contract) {
    const tier: CalloutTier = input.tier === "care" || input.tier === "complete" ? input.tier : "core";
    baseFirst = IN_CONTRACT_FIRST[tier];
    baseAdd = IN_CONTRACT_ADDITIONAL[tier];
    travelRate = TRAVEL_PER_HOUR_IN_CONTRACT; // flat -- never doubles, any time of day
  } else {
    baseFirst = NON_CONTRACT_FIRST;
    baseAdd = NON_CONTRACT_ADDITIONAL;
    // Session AKN: out of hours doubles non-contract travel again, exactly like labour
    // (£120/hr -> £240/hr). Waivable per job via the office's agreed-price field.
    travelRate = input.outOfHours ? TRAVEL_PER_HOUR_NON_CONTRACT * 2 : TRAVEL_PER_HOUR_NON_CONTRACT;
  }

  const firstHourNet = input.outOfHours ? round2(baseFirst * 2) : baseFirst;
  const additionalHourNet = input.outOfHours ? round2(baseAdd * 2) : baseAdd;
  const travelNet = round2(travelRate * (travelMins / 60));
  const totalNet = round2(firstHourNet + travelNet);
  return { bundled: false, firstHourNet, additionalHourNet, travelNet, totalNet, totalGross: round2(totalNet * (1 + VAT_RATE)) };
}

export function gbp(n: number): string {
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ---- Agreed price (Session AEW) --------------------------------------------
// The office types a one-off agreed price in POUNDS on the log form; the API takes PENCE, the
// unit every money column on CallOut uses. This is the one conversion, kept pure so the page and
// its tests share it and so the boundary cases are stated once rather than guessed per caller.
//
// It mirrors the server bound (agreedPrice.ts AGREED_PRICE_MAX_PENCE). The server is
// authoritative and revalidates; this exists so the office is told what is wrong while typing
// rather than by a 400 after pressing the button.
export const AGREED_PRICE_MAX_POUNDS = 100_000;

export type AgreedPriceParse =
  | { state: "blank"; pence: null } // no agreed price: the computed rate applies
  | { state: "ok"; pence: number } // a figure to bill (0 means agreed free of charge)
  | { state: "invalid"; pence: null; message: string };

export function parseAgreedPrice(raw: string): AgreedPriceParse {
  const trimmed = raw.trim();
  if (trimmed === "") return { state: "blank", pence: null };
  // Reject anything that is not a plain money figure BEFORE Number() gets it: Number("")
  // is 0, Number(" 1e3 ") is 1000 and Number("0x10") is 16, none of which is a price
  // someone typed. Optional leading pound sign and thousands separators are forgiven,
  // because an office user pasting "1,250.00" from an email means exactly what it says.
  const cleaned = trimmed.replace(/^£/, "").replace(/,/g, "").trim();
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) {
    return { state: "invalid", pence: null, message: "Enter the agreed price as a number, for example 250 or 250.00." };
  }
  const pounds = Number(cleaned);
  if (!Number.isFinite(pounds) || pounds > AGREED_PRICE_MAX_POUNDS) {
    return {
      state: "invalid",
      pence: null,
      message: `Enter an agreed price of £${AGREED_PRICE_MAX_POUNDS.toLocaleString("en-GB")} or less, or leave it blank to use the standard rate.`,
    };
  }
  // The regex has already limited this to two decimal places, so the multiply is exact to the
  // penny; the round is belt and braces against binary floating point (2.67 * 100 = 266.999...).
  return { state: "ok", pence: Math.round(pounds * 100) };
}
